import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import type { Lang } from "@/content/tracks";

type PackageSlug = "strategic-mentoring" | "turnaround-sprint" | "board-readiness";

interface Props {
  slug: PackageSlug;
  title: { en: string; fr: string };
  subtitle: { en: string; fr: string };
  description: { en: string; fr: string };
  whoItsFor: { en: string[]; fr: string[] };
  duration: { en: string; fr: string };
  price: { en: string; fr: string } | null;
  priceIndicator: { en: string; fr: string } | null;
  lang: Lang;
  onApply: () => void;
}

export default function MentorPackageCard({
  title,
  subtitle,
  description,
  whoItsFor,
  duration,
  price,
  priceIndicator,
  lang,
  onApply,
}: Props) {
  const resolved: "en" | "fr" = lang === "fr" ? "fr" : "en";
  const applyLabel = resolved === "fr" ? "Candidater" : "Apply";

  return (
    <div
      className="flex flex-col rounded-2xl border p-7 transition hover:-translate-y-[3px]"
      style={{
        backgroundColor: "hsl(var(--learn-surface))",
        borderColor: "hsl(var(--learn-border))",
      }}
    >
      {/* BOOTPRENEURS pill */}
      <div className="flex items-center gap-2">
        <span
          className="inline-flex items-center rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-white"
          style={{
            background: "linear-gradient(135deg, hsl(var(--learn-primary)), hsl(var(--learn-primary-4)))",
          }}
        >
          BOOTPRENEURS
        </span>
        <span
          className="inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider"
          style={{
            borderColor: "hsl(var(--learn-border))",
            color: "hsl(var(--learn-text-muted))",
          }}
        >
          {duration[resolved]}
        </span>
      </div>

      {/* Title */}
      <h3
        className="mt-5 font-display text-2xl font-bold tracking-tight"
        style={{ color: "hsl(var(--learn-text))" }}
      >
        {title[resolved]}
      </h3>
      <p
        className="mt-1.5 text-sm italic"
        style={{ color: "hsl(var(--learn-primary-2))" }}
      >
        {subtitle[resolved]}
      </p>

      {/* Description */}
      <p
        className="mt-4 text-sm leading-relaxed"
        style={{ color: "hsl(var(--learn-text-muted))" }}
      >
        {description[resolved]}
      </p>

      {/* Who it's for */}
      <ul className="mt-5 space-y-2">
        {whoItsFor[resolved].map((item, i) => (
          <li key={i} className="flex gap-2 text-sm" style={{ color: "hsl(var(--learn-text))" }}>
            <Check className="mt-0.5 h-4 w-4 flex-shrink-0" style={{ color: "hsl(var(--learn-primary))" }} />
            <span>{item}</span>
          </li>
        ))}
      </ul>

      {/* Spacer pushes price+CTA to the bottom */}
      <div className="flex-1" />

      {/* Price */}
      <div className="mt-6 border-t pt-5" style={{ borderColor: "hsl(var(--learn-border))" }}>
        <div
          className="font-display text-lg font-bold"
          style={{ color: "hsl(var(--learn-text))" }}
        >
          {price ? price[resolved] : priceIndicator ? priceIndicator[resolved] : ""}
        </div>
        <Button onClick={onApply} className="mt-4 w-full" size="lg">
          {applyLabel}
        </Button>
      </div>
    </div>
  );
}
